// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { useState } from 'react';
import { messages, useMessages } from '../i18n.js';

/**
 * One approved interaction, rendered (ADR-028).
 *
 * Its type always came from the closed registry — the page never sees a shape the application did
 * not write — so this component switches over known kinds and nothing else. Two rules are structural
 * rather than styled: a person may always answer in their own words, and may always decline. A panel
 * without those is a form, and a form is not a conversation.
 */
export interface InteractionView {
  readonly id: string;
  readonly type:
    'quick_choices' | 'understanding_check' | 'editable_plan' | 'priority_board' | 'commitment';
  readonly title: string;
  readonly options: readonly { readonly id: string; readonly label: string }[];
}

/** Where a person can put something that is on them. Fixed, and the application's, not the model's. */
const BUCKET_IDS = ['now', 'later', 'delegate', 'drop'] as const;

const T = messages({
  ar: {
    buckets: { now: 'الآن', later: 'لاحقًا', delegate: 'أفوّضها', drop: 'أتركها' },
    step: (n: number) => `الخطوة ${String(n)}`,
    moveUp: (n: number) => `حرّك الخطوة ${String(n)} لأعلى`,
    moveDown: (n: number) => `حرّك الخطوة ${String(n)} لأسفل`,
    restore: (n: number) => `أعد الخطوة ${String(n)}`,
    remove: (n: number) => `احذف الخطوة ${String(n)}`,
    keepOne: 'أبقِ خطوة واحدة على الأقل',
    sendPlan: 'أرسل الخطة',
    sendOrder: 'أرسل الترتيب',
    placeAll: 'رتّب كل بند لإرساله',
    orType: 'أو اكتب إجابتك بكلماتك في الأسفل',
    skip: 'تخطَّ هذا',
    queued: 'سيُرسَل بعد انتهاء الرد الحالي',
  },
  en: {
    buckets: { now: 'Now', later: 'Later', delegate: 'Delegate', drop: 'Drop' },
    step: (n: number) => `Step ${String(n)}`,
    moveUp: (n: number) => `Move step ${String(n)} up`,
    moveDown: (n: number) => `Move step ${String(n)} down`,
    restore: (n: number) => `Restore step ${String(n)}`,
    remove: (n: number) => `Remove step ${String(n)}`,
    keepOne: 'Keep at least one step',
    sendPlan: 'Send the plan',
    sendOrder: 'Send the order',
    placeAll: 'Place every item to send it',
    orType: 'Or answer in your own words below',
    skip: 'Skip this',
    queued: 'It will be sent once the current reply finishes',
  },
});

/** A step of a plan while the person is still shaping it. Local until they send it. */
export interface PlanStep {
  readonly id: string;
  readonly label: string;
  readonly dropped: boolean;
  /** True once the person has rewritten the words, so the event can say whose they are. */
  readonly edited: boolean;
}

export function InteractionPanel({
  interaction,
  busy,
  onChoose,
  onSort,
  onPlan,
  onSkip,
}: {
  readonly interaction: InteractionView;
  /** A choice made while the previous turn is still running is queued, never lost. */
  readonly busy: boolean;
  readonly onChoose: (optionId: string, label: string) => void;
  /** Sorting sends one event carrying every placement, not one event per drag. */
  readonly onSort: (
    placements: readonly { readonly optionId: string; readonly bucket: string }[],
  ) => void;
  /** A plan is sent once, whole: an event per keystroke would be a conversation with a form. */
  readonly onPlan: (steps: readonly PlanStep[]) => void;
  readonly onSkip: () => void;
}): React.JSX.Element {
  const t = useMessages(T);
  const [placed, setPlaced] = useState<Readonly<Record<string, string>>>({});
  const sorting = interaction.type === 'priority_board';
  const planning = interaction.type === 'editable_plan';
  const [plan, setPlan] = useState<readonly PlanStep[]>(() =>
    interaction.options.map((option) => ({
      id: option.id,
      label: option.label,
      dropped: false,
      edited: false,
    })),
  );
  const kept = plan.filter((step) => !step.dropped);
  const move = (index: number, by: -1 | 1): void => {
    setPlan((current) => {
      const next = [...current];
      const moving = next[index];
      const displaced = next[index + by];
      if (moving === undefined || displaced === undefined) return current;
      next[index] = displaced;
      next[index + by] = moving;
      return next;
    });
  };
  const allPlaced = interaction.options.every((option) => placed[option.id] !== undefined);

  return (
    <section
      className={`interaction interaction-${interaction.type}`}
      aria-label={interaction.title}
    >
      <p className="interaction-title">{interaction.title}</p>

      {planning ? (
        <ol className="interaction-plan">
          {plan.map((step, index) => (
            <li
              key={step.id}
              className={step.dropped ? 'interaction-step dropped' : 'interaction-step'}
            >
              <span className="interaction-step-order">{index + 1}</span>
              <input
                type="text"
                className="interaction-step-text"
                value={step.label}
                aria-label={t.step(index + 1)}
                disabled={step.dropped}
                onChange={(changed) => {
                  // Their words stay theirs: the event names the lines they rewrote, so the plan is
                  // never filed as something Bilal proposed and they merely accepted.
                  const written = changed.target.value;
                  setPlan((current) =>
                    current.map((entry) =>
                      entry.id === step.id ? { ...entry, label: written, edited: true } : entry,
                    ),
                  );
                }}
              />
              <span className="interaction-step-controls">
                <button
                  type="button"
                  className="interaction-step-move"
                  aria-label={t.moveUp(index + 1)}
                  disabled={index === 0}
                  onClick={() => {
                    move(index, -1);
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="interaction-step-move"
                  aria-label={t.moveDown(index + 1)}
                  disabled={index === plan.length - 1}
                  onClick={() => {
                    move(index, 1);
                  }}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="interaction-step-drop"
                  aria-pressed={step.dropped}
                  aria-label={step.dropped ? t.restore(index + 1) : t.remove(index + 1)}
                  onClick={() => {
                    // Removed, never erased: a step can come back, because a plan is a draft.
                    setPlan((current) =>
                      current.map((entry) =>
                        entry.id === step.id ? { ...entry, dropped: !entry.dropped } : entry,
                      ),
                    );
                  }}
                >
                  {step.dropped ? '↺' : '×'}
                </button>
              </span>
            </li>
          ))}
        </ol>
      ) : sorting ? (
        <ul className="interaction-board">
          {interaction.options.map((option) => (
            <li key={option.id} className="interaction-row">
              <span className="interaction-row-label">{option.label}</span>
              <span className="interaction-buckets">
                {BUCKET_IDS.map((bucket) => (
                  <button
                    key={bucket}
                    type="button"
                    className="interaction-bucket"
                    aria-pressed={placed[option.id] === bucket}
                    onClick={() => {
                      // Placements are held here until they are sent together: a half-sorted board
                      // is a thought in progress, not an answer.
                      setPlaced((current) => ({ ...current, [option.id]: bucket }));
                    }}
                  >
                    {t.buckets[bucket]}
                  </button>
                ))}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="interaction-options">
          {interaction.options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="interaction-option"
              onClick={() => {
                onChoose(option.id, option.label);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {planning && (
        <button
          type="button"
          className="interaction-send"
          disabled={kept.length === 0 || kept.some((step) => step.label.trim().length === 0)}
          onClick={() => {
            onPlan(plan);
          }}
        >
          {kept.length === 0 ? t.keepOne : t.sendPlan}
        </button>
      )}

      {sorting && (
        <button
          type="button"
          className="interaction-send"
          disabled={!allPlaced}
          onClick={() => {
            onSort(
              interaction.options.map((option) => ({
                optionId: option.id,
                bucket: placed[option.id] ?? 'later',
              })),
            );
          }}
        >
          {allPlaced ? t.sendOrder : t.placeAll}
        </button>
      )}

      <div className="interaction-foot">
        {/* Never withheld: the model cannot switch either of these off. */}
        <span className="interaction-hint">{t.orType}</span>
        <button type="button" className="interaction-skip" onClick={onSkip}>
          {t.skip}
        </button>
      </div>
      {busy && <p className="interaction-busy">{t.queued}</p>}
    </section>
  );
}
